import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { Box, Button } from '@mui/material';
import { useCallback, useRef } from 'react';

// Read only tree of the Contract's composition, sourced from CtrtOutlineDataPlugin's pipe-delimited paths
export const CtrtOutline = ({ ctrtId, focusEntity, focusEntityId }) => {
  const rsrc = useRsrc();
  const gridRef = useRef(null);

  const gridApi = () => gridRef.current?.gridRef?.current?.gridApiRef?.current;

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    ObjUtils.clearObj(fieldToFieldDef);

    fieldToFieldDef.name = { field: 'name', displayName: 'Name', autoGrouping: true, minWidth: 320 };
    fieldToFieldDef.value = { field: 'value', displayName: 'Value', flex: 1, type: 'ENTITY_LINK', module: 'md' };
  }, []);

  // Opens only the branch leading to the Entity the outline was launched from, then scrolls to it
  const expandToFocus = useCallback((focusPath) => {
    const api = gridApi();
    if (!api || !focusPath) return;

    let focusNode;
    api.forEachNode((node) => {
      const path = node.data?.path;
      if (!path) return;
      if (path === focusPath) {
        focusNode = node;
      } else if (focusPath.startsWith(path + '|')) {
        node.setExpanded(true);
      }
    });

    if (!focusNode) return;
    focusNode.setExpanded(true);
    api.ensureNodeVisible(focusNode, 'middle');
  }, []);

  const clientSideRowData = useCallback(
    (setDataSource) => {
      if (!ctrtId) {
        setDataSource([]);
        return;
      }
      rsrc.data('md', 'CTRT_OUTLINE_DATA_PLUGIN', { ctrtId }).then((rows) => {
        setDataSource(rows);

        if (!focusEntityId) return;
        const focusRow = rows.find(
          (row) => row.linkEntity === focusEntity && String(row.linkEntityId) === String(focusEntityId),
        );
        // Expanded after setDataSource so the Grid's restore of previously opened rows cannot collapse the branch again
        expandToFocus(focusRow?.path);
      });
    },
    [ctrtId, focusEntity, focusEntityId, expandToFocus],
  );

  const handleExpandAll = () => {
    gridApi()?.expandAll();
  };

  const handleCollapseAll = () => {
    gridApi()?.collapseAll();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <Box sx={{ mb: 1, display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
        <Button variant="outlined" size="small" onClick={handleExpandAll}>
          Expand
        </Button>
        <Button variant="outlined" size="small" onClick={handleCollapseAll}>
          Collapse
        </Button>
      </Box>
      <EntityList
        ref={gridRef}
        module="md"
        entity="CTRT"
        showTitle={false}
        augFieldDefs={augFieldDefs}
        clientSideRowData={clientSideRowData}
        gridOptions={{
          treeData: true,
          groupDefaultExpanded: focusEntityId ? 0 : 2,
          // Rows span several entities, so the default row level navigation does not apply
          getContextMenuItems: () => [],
        }}
      />
    </Box>
  );
};
